/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import { composedPathContains } from "@/utils/getComposedEventTarget";

import { AiVariableOption } from "./aiConfig.types";
import {
	buildAiVariableChipHtml,
	editorHtmlToPromptText,
	filterAiVariableOptions,
	groupAiVariableOptions,
	promptToEditorHtml,
} from "./aiPromptEditor.utils";

interface AiPromptEditorProps {
	value: string;
	placeholder: string;
	error?: string;
	insertVariableLabel: string;
	metaLabel: string;
	variablesLabel: string;
	variableOptions: AiVariableOption[];
	onChange: (value: string) => void;
}

export const AiPromptEditor: FunctionComponent<AiPromptEditorProps> = ({
	value,
	placeholder,
	error,
	insertVariableLabel,
	metaLabel,
	variablesLabel,
	variableOptions,
	onChange,
}) => {
	const editorRef = useRef<any>(null);
	const bookmarkRef = useRef<any>(null);
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const initialEditorValueRef = useRef(promptToEditorHtml(value));
	const lastEmittedPromptRef = useRef(value);
	// The TinyMCE editor's `setup` callback captures variableOptions in its closure once at mount.
	// Use a ref to expose the latest list so the autocompleter (`{` trigger) stays in sync when
	// the parent updates the list (e.g. when the target field's allowed values become available).
	const variableOptionsRef = useRef(variableOptions);
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const filteredOptions = useMemo(
		() => filterAiVariableOptions(variableOptions, searchValue),
		[searchValue, variableOptions]
	);
	const groupedOptions = useMemo(() => groupAiVariableOptions(filteredOptions), [filteredOptions]);

	useEffect(() => {
		variableOptionsRef.current = variableOptions;
	}, [variableOptions]);

	useEffect(() => {
		if (!isPickerOpen) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			if (composedPathContains(popoverRef.current, event)) {
				return;
			}

			setIsPickerOpen(false);
		};

		window.addEventListener("click", handleClickOutside);

		return () => window.removeEventListener("click", handleClickOutside);
	}, [isPickerOpen]);

	useEffect(() => {
		if (!editorRef.current || lastEmittedPromptRef.current === value) {
			return;
		}

		const currentPrompt = editorHtmlToPromptText(editorRef.current.getContent());

		if (currentPrompt === value) {
			lastEmittedPromptRef.current = value;

			return;
		}

		const bookmark = editorRef.current.selection?.getBookmark?.(2, true);

		editorRef.current.setContent(promptToEditorHtml(value));
		lastEmittedPromptRef.current = value;

		if (bookmark) {
			editorRef.current.selection?.moveToBookmark?.(bookmark);
		}
	}, [value]);

	const openVariablePicker = () => {
		bookmarkRef.current = editorRef.current?.selection?.getBookmark?.(2, true);
		setIsPickerOpen((current) => !current);
	};

	const insertVariable = (option: AiVariableOption) => {
		if (!editorRef.current) {
			onChange(`${value}${value ? " " : ""}${option.token}`);
			setIsPickerOpen(false);

			return;
		}

		editorRef.current.focus();
		if (bookmarkRef.current) {
			editorRef.current.selection?.moveToBookmark?.(bookmarkRef.current);
		}

		editorRef.current.insertContent(buildAiVariableChipHtml(option));
		setIsPickerOpen(false);
		setSearchValue("");
	};

	return (
		<div className="rounded-2xl border border-border-light bg-brand-white">
			<div className="flex items-center justify-between gap-3 border-b border-border-light bg-background p-3">
				<div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-meta">
					{metaLabel}
				</div>
				<div className="relative" ref={popoverRef}>
					<Button
						className="!h-[30px] !w-auto !rounded-lg !px-3"
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						title={insertVariableLabel}
						type="button"
						onClick={openVariablePicker}
					/>
					{isPickerOpen && (
						<div className="absolute right-0 top-[calc(100%+8px)] z-30 w-[320px] rounded-2xl border border-border-light bg-brand-white p-3 shadow-xl">
							<input
								className="mb-3 w-full rounded-xl border border-border-light bg-brand-white px-3 py-2 text-sm text-text outline-none"
								placeholder={variablesLabel}
								value={searchValue}
								onChange={(event) => setSearchValue(event.target.value)}
							/>
							<div className="max-h-[260px] overflow-y-auto pr-1">
								{Object.entries(groupedOptions).map(([group, options]) => (
									<div key={group} className="mb-3 last:mb-0">
										<div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-text-meta">
											{group}
										</div>
										<div className="space-y-1">
											{options.map((option) => (
												<button
													key={option.key}
													className="w-full rounded-xl border border-transparent bg-background px-3 py-2 text-left text-sm text-text transition hover:border-border-light hover:bg-brand-white"
													type="button"
													onClick={() => insertVariable(option)}
												>
													<div className="font-medium">{option.label}</div>
													<div className="mt-0.5 text-xs text-text-meta">{option.token}</div>
												</button>
											))}
										</div>
									</div>
								))}
								{!filteredOptions.length && (
									<div className="rounded-xl bg-background px-3 py-4 text-center text-sm text-text-meta">
										{variablesLabel}
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
			<div
				className={classNames(
					"overflow-hidden rounded-b-2xl",
					error ? "ring-1 ring-status-error" : ""
				)}
			>
				<Editor
					apiKey={import.meta.env.VITE_TINY_MCE}
					init={{
						autoresize_bottom_margin: 0,
						branding: false,
						content_style: `
							body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #111827; }
							.ai-variable-chip { background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 9999px; color: #4d66f5; padding: 2px 8px; font-weight: 600; }
						`,
						forced_root_block: false,
						height: 220,
						menubar: false,
						placeholder,
						plugins: "autoresize noneditable paste",
						promotion: false,
						resize: false,
						statusbar: false,
						toolbar: false,
						setup: (editor: any) => {
							editorRef.current = editor;
							editor.ui.registry.addAutocompleter("aiVariables", {
								trigger: "{",
								minChars: 0,
								matches: () => true,
								fetch: (pattern: string) => {
									return Promise.resolve(
										filterAiVariableOptions(variableOptionsRef.current, pattern).map((option) => ({
											text: `${option.label} · ${option.token}`,
											type: "autocompleteitem",
											value: option.key,
										}))
									);
								},
								onAction: (api: any, rng: any, selectedKey: string) => {
									const option = variableOptionsRef.current.find(
										(item) => item.key === selectedKey
									);

									if (!option) {
										return;
									}

									editor.selection.setRng(rng);
									editor.insertContent(buildAiVariableChipHtml(option));
									api.hide();
								},
							});
						},
					}}
					initialValue={initialEditorValueRef.current}
					onEditorChange={(content: string) => {
						const nextPrompt = editorHtmlToPromptText(content);

						lastEmittedPromptRef.current = nextPrompt;
						onChange(nextPrompt);
					}}
				/>
			</div>
			{error && (
				<div className="border-t border-border-light px-3 py-2 text-xs text-status-error">
					{error}
				</div>
			)}
		</div>
	);
};
